import React, { FC, useState } from 'react'
import { useParams } from 'react-router-dom'
import { TBaseValue } from 'transfix-ui/components/AutocompleteTransfixUser'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics/useFMSAnalytics'
import { ITrailer, useUpdateSharedShipment } from 'apps/fleet-planner/types'
import { Button, Spinner } from 'transfix-ui'
import { FullPageOverlay } from '../modals/full-page-overlay/FullPageOverlay'
import { AutocompleteTrailerNumber } from './components/AutocompleteTrailerNumber/AutocompleteTrailerNumber'

export interface ITrailerNumberFormProps {
  isOpen: boolean
  onClose: () => void
  shareToken: string
  trailer?: ITrailer | null | undefined
}

export const TrailerNumberForm: FC<ITrailerNumberFormProps> = ({
  isOpen,
  onClose,
  shareToken,
  trailer,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const { id: loadId = '' } = useParams<{ id: string }>()
  const { trailerNumber: defaultNumber } = trailer || {}

  const defaultTrailerNumber = defaultNumber
    ? { id: defaultNumber, text: defaultNumber, groupIndex: 0 }
    : null

  const [trailerNumber, setTrailerNumber] = useState<TBaseValue | null>(defaultTrailerNumber)

  const { loading: isTrailerNumberUpdating, mutate: updateTrailerNumber } = useUpdateSharedShipment(
    {
      id: parseInt(loadId, 10),
      queryParams: { shareToken },
    }
  )

  const submitFormHandler = async () => {
    trackEvent('Driver Led Status', 'DRIVER_LED_STATUS_DRIVERS_TRAILER_NUMBER_SUBMIT')
    const { id } = trailerNumber || {}
    if (id) {
      await updateTrailerNumber({
        shipment: {
          trailerNumber: id,
        },
      }).then(() => {
        // refresh the page
        window.location.reload()
      })
      onClose()
    }
  }

  const { isMobile } = useBreakpointContext()

  const onTrailerNumberChange = (newVal: TBaseValue | null) => {
    setTrailerNumber(newVal)
  }

  return (
    <FullPageOverlay
      title='Submit your trailer info'
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submitFormHandler}
      footer={
        <>
          <Button
            fullWidth={isMobile}
            color='tertiary'
            size='large'
            onClick={onClose}
            data-test='skip-trailer-number'>
            Skip
          </Button>
          <Button
            color='primary'
            fullWidth={isMobile}
            size='large'
            onClick={submitFormHandler}
            disabled={isTrailerNumberUpdating}
            data-test='submit-trailer-number'>
            {isTrailerNumberUpdating ? 'Loading...' : 'Submit'}
          </Button>
        </>
      }>
      <div className='body-medium'>Trailer Details</div>
      <div className='mt4'>
        {isTrailerNumberUpdating ? (
          <Spinner position='absolute' />
        ) : (
          <AutocompleteTrailerNumber
            value={trailerNumber}
            onChange={onTrailerNumberChange}
            shipmentId={loadId}
            shareToken={shareToken}
          />
        )}
      </div>
    </FullPageOverlay>
  )
}
